import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Función para verificar y cargar sesión
    const loadSession = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      const savedLicense = localStorage.getItem('license');

      if (token && savedUser) {
        try {
          // Verificar que el token sea válido llamando al backend
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            // Token válido, usar datos del backend (más confiables)
            const data = await response.json().catch(() => null);
            
            if (!data) {
              // Si no hay data, limpiar sesión
              console.warn('No se pudo parsear la respuesta, limpiando sesión...');
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              localStorage.removeItem('license');
              setUser(null);
              setLicense(null);
              setLoading(false);
              return;
            }
            
            setUser(data.user);
            
            // Actualizar license desde backend
            if (data.license) {
              setLicense(data.license);
              localStorage.setItem('license', JSON.stringify(data.license));
            } else {
              setLicense(null);
              localStorage.removeItem('license');
            }
          } else {
            // Token inválido o expirado, limpiar todo
            console.warn('Token inválido o expirado, limpiando sesión...');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('license');
            setUser(null);
            setLicense(null);
          }
        } catch (error) {
          // Error de red o backend, usar datos de localStorage como fallback
          console.warn('Error verificando token, usando localStorage como fallback:', error);
          setUser(JSON.parse(savedUser));
          if (savedLicense) {
            try {
              const parsedLicense = JSON.parse(savedLicense);
              // Validar que la licencia tenga plan_type o license_type
              if (parsedLicense && (parsedLicense.plan_type || parsedLicense.license_type)) {
                setLicense(parsedLicense);
              } else {
                // Licencia corrupta o legacy sin plan_type, limpiar
                console.warn('Licencia sin plan_type detectada, limpiando...');
                localStorage.removeItem('license');
                setLicense(null);
              }
            } catch (e) {
              console.error('Error parsing license:', e);
              localStorage.removeItem('license');
              setLicense(null);
            }
          }
        }
      }
      
      setLoading(false);
    };
    
    loadSession();
  }, []);

  const login = (token, userData, licenseData) => {
    // Guardar en localStorage PRIMERO (síncrono)
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    
    if (licenseData) {
      localStorage.setItem('license', JSON.stringify(licenseData));
    } else {
      localStorage.removeItem('license');
    }
    
    // Actualizar states DESPUÉS (puede ser asíncrono)
    setUser(userData);
    setLicense(licenseData || null);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('license');
    setUser(null);
    setLicense(null);
  };

  const updateLicense = (licenseData) => {
    localStorage.setItem('license', JSON.stringify(licenseData));
    setLicense(licenseData);
  };

  const updateUser = (userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, license, loading, login, logout, updateLicense, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
